import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Icons, Spinner } from '../../components/ui.tsx';
import { FileTypeIcon } from '../../components/icons.tsx';
import { formatBytes } from '../../lib/display.ts';
import { SharedFolderPage } from './SharedFolderPage.tsx';
import { GuestRecordViewer } from './GuestRecordViewer.tsx';

/**
 * What somebody sees when they open a shared link.
 *
 * Deliberately outside the application: no sidebar, no search, no sign-in
 * prompt, nothing that suggests there is a system behind this. The recipient
 * is a customer who was sent an invoice, not a user who has forgotten their
 * password, and a page that asks them to log in is a page they will reply to
 * by email instead.
 *
 * It fetches without credentials for the same reason it exists: this is the
 * one route in the app that must behave identically for a stranger and for the
 * owner, and sending a session cookie would make those two cases different in
 * a way nobody would notice until it mattered.
 */

interface SharedItem {
  name: string;
  mimeType: string;
  sizeBytes: number | null;
  allowDownload: boolean;
  kind: 'FILE' | 'DOCUMENT' | 'SPREADSHEET' | 'FOLDER';
}

export function SharedPage() {
  const { token = '' } = useParams();
  const [item, setItem] = useState<SharedItem | null>(null);
  const [failed, setFailed] = useState(false);
  const [content, setContent] = useState<string | null>(null);

  /*
   * Not for search engines.
   *
   * The API says so on both of its routes, but this page is the application's
   * own HTML and a crawler that found a link posted somewhere public would
   * index it on that alone.
   */
  useEffect(() => {
    const tag = document.createElement('meta');
    tag.name = 'robots';
    tag.content = 'noindex, nofollow';
    document.head.append(tag);
    return () => tag.remove();
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(`/api/public/${encodeURIComponent(token)}`, {
          // Omitted rather than left to the default, which is same-origin and
          // would send the owner's session cookie with a request meant to be
          // anonymous. The route ignores it either way; the point is that this
          // page behaves identically for a stranger and for the person who
          // made the link, and that should be true rather than nearly true.
          credentials: 'omit',
        });
        if (!response.ok) throw new Error('gone');
        const body = (await response.json()) as SharedItem;
        if (!cancelled) setItem(body);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  /*
   * The bytes are fetched and framed as a blob, not pointed at with a src.
   *
   * The site's security headers say no HTTP response from this origin may be
   * rendered inside a frame — `X-Frame-Options: DENY` and `frame-ancestors
   * 'none'`, set once in front of everything, which is the right default for a
   * system holding a company's books. A same-origin page is no exception, so
   * an iframe pointed at the content URL came up blank on the server while
   * working perfectly in development, where those headers are laxer.
   *
   * A blob URL is not an HTTP response and carries no such headers, which is
   * why the Drive's own file viewer has always fetched and framed one — and
   * why the site policy already allows `frame-src 'self' blob:`. This is that
   * same move, and it means the public page needs nothing loosened for it.
   *
   * The cost is honest: the file is held in memory while the page is open.
   * The viewer inside the app makes the same trade for the same reason.
   */
  useEffect(() => {
    /*
     * Only a file is fetched as bytes.
     *
     * A folder has none — asking for them would 404 and take the page down
     * with it, which is exactly what it did — and a letter or a workbook is
     * drawn from its record below, so fetching the PDF as well would spend a
     * render nobody is going to look at.
     */
    if (!item || item.kind !== 'FILE') return undefined;

    let objectUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(`/api/public/${encodeURIComponent(token)}/content`, {
          credentials: 'omit',
        });
        if (!response.ok) throw new Error('gone');

        objectUrl = URL.createObjectURL(await response.blob());
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setContent(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      // Or the browser holds the whole file for the life of the tab.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item, token]);

  if (failed) {
    return (
      <Centred>
        <h1 className="text-h2 text-ink">This link is no longer available</h1>
        <p className="mt-1 max-w-md text-body-sm text-muted">
          It may have been turned off by the person who shared it, or it may have expired. Ask them
          for a new one.
        </p>
      </Centred>
    );
  }

  if (!item) {
    return (
      <Centred>
        <Spinner label="Opening" />
      </Centred>
    );
  }

  /*
   * A folder is a different page, decided here rather than by the URL.
   *
   * The link the client sends is the same shape whatever is behind it — they
   * copy one thing and paste it — so what it opens is a fact about the link,
   * discovered when it is resolved. A folder needs a listing, breadcrumbs and
   * whatever the level allows; a file needs a viewer. Two pages, one address.
   */
  if (item.kind === 'FOLDER') return <SharedFolderPage token={token} />;

  /*
   * A letter or a workbook is shown as itself, in the app's own editor with
   * writing switched off — not as a PDF of itself.
   *
   * The PDF render is still what the frame below shows for an uploaded file,
   * and it is still what the export produces. It is the wrong thing to hand
   * somebody as the shared item, though: a workbook flattened to a page has no
   * tabs, no formulas and nothing to sort, and a letter becomes something you
   * cannot copy a line out of. What was shared is the work.
   */
  if (item.kind === 'DOCUMENT' || item.kind === 'SPREADSHEET') {
    return (
      <GuestRecordViewer
        src={`/api/public/${encodeURIComponent(token)}/record`}
        downloadHref={
          item.allowDownload
            ? `/api/public/${encodeURIComponent(token)}/content?download=1`
            : undefined
        }
        name={item.name}
        kind={item.kind === 'DOCUMENT' ? 'document' : 'spreadsheet'}
      />
    );
  }

  const shown = !content ? (
    <Spinner label="Opening" />
  ) : item.mimeType.startsWith('image/') ? (
    <img src={content} alt={item.name} className="max-h-full max-w-full object-contain" />
  ) : item.mimeType.startsWith('video/') ? (
    <video src={content} controls className="max-h-full max-w-full" />
  ) : item.mimeType === 'application/pdf' ? (
    // The whole page, no thumbnail rail — the same fragment the Drive's own
    // viewer uses, and for the same reasons.
    <iframe
      src={`${content}#view=Fit&navpanes=0`}
      title={item.name}
      className="h-full w-full border-0"
    />
  ) : (
    <div className="flex flex-col items-center gap-3 p-10 text-center">
      <FileTypeIcon name={item.name} mimeType={item.mimeType} size={56} />
      <p className="text-body-sm text-ink">This kind of file cannot be shown in a browser.</p>
      {item.allowDownload ? (
        <p className="text-micro text-muted">Use Download to open it on your computer.</p>
      ) : null}
    </div>
  );

  return (
    <div className="flex h-screen flex-col bg-[rgb(15,23,36)]">
      <header className="flex shrink-0 items-center gap-3 px-4 py-3">
        <FileTypeIcon name={item.name} mimeType={item.mimeType} size={22} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-body-sm font-medium text-white">{item.name}</p>
          <p className="text-micro text-[var(--blue-200)]">
            {item.sizeBytes === null
              ? 'Shared with you'
              : `${formatBytes(item.sizeBytes)} · shared with you`}
          </p>
        </div>

        {/* A plain navigation, not a frame: the headers that stop a response
            being framed have nothing to say about a download. */}
        {item.allowDownload ? (
          <a
            href={`/api/public/${encodeURIComponent(token)}/content?download=1`}
            download={item.name}
          >
            <Button
              icon={<Icons.Download />}
              className="border-white/25 bg-white/10 text-white hover:bg-white/20"
            >
              Download
            </Button>
          </a>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 pb-2 max-md:px-0 md:px-4 md:pb-4">
        <div className="flex h-full items-center justify-center overflow-hidden rounded-xl bg-surface">
          {shown}
        </div>
      </div>

      <footer className="shrink-0 px-4 pb-3 text-center text-micro text-white/50">
        Shared from the Abdul Qadir Finance System. You are seeing a copy; you cannot change it.
      </footer>
    </div>
  );
}

function Centred({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-screen flex-col items-center justify-center bg-canvas px-6 text-center">
      {children}
    </div>
  );
}
